import { useEffect, useRef, useState } from 'preact/hooks';
import type { Product } from './eto-app';
import CustomSelect from './custom-select';

function setCenteredMaxWidth(anchor: HTMLElement, target: HTMLElement, customProperty: string, viewportPadding = 16) {
    const { left, width } = anchor.getBoundingClientRect();
    const centerX = left + (width / 2);
    const spaceLeft = centerX - viewportPadding;
    const spaceRight = (window.innerWidth - viewportPadding) - centerX;
    const maxWidth = Math.floor(Math.max(0, 2 * Math.min(spaceLeft, spaceRight)));
    target.style.setProperty(customProperty, `${maxWidth}px`);
    return maxWidth;
}

function getPaginationErrorMessage(input: HTMLInputElement) {
    const { validity } = input;

    if (validity.rangeUnderflow) {
        return `${input.dataset.errorMin}  ${input.min}`;
    }
    if (validity.rangeOverflow) {
        return `${input.dataset.errorMax}  ${input.max}`;
    }
    if (validity.badInput || validity.typeMismatch) {
        return input.dataset.errorNumber || '';
    }
    return input.validationMessage;
}

function ComponentsSection({ componentName, serialNumber, componentsLoading, error, components, pageNumber, setPageNumber, pageSize, setPageSize, totalPages, toTitleCase, i18n }: {
    componentName: string;
    serialNumber: string;
    componentsLoading: boolean;
    error: string | null;
    components: Product[];
    pageNumber: number;
    setPageNumber: (pageNumber: number) => void;
    pageSize: number;
    setPageSize: (pageSize: number) => void;
    totalPages: number;
    toTitleCase: (string: string) => string;
    i18n: (key: string) => string;
}) {
    const paginationErrorId = `${componentName}__pagination-error`;
    const inputRef = useRef<HTMLInputElement>(null);
    const errorRef = useRef<HTMLDivElement>(null);
    const pageNumberRef = useRef(pageNumber);
    const errorVisibleRef = useRef(false);
    const restoreHandlerRef = useRef<((event: Event) => void) | null>(null);
    const [errorVisible, setErrorVisible] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    pageNumberRef.current = pageNumber;
    errorVisibleRef.current = errorVisible;

    const hidePaginationError = () => {
        setErrorVisible(false);
        setTimeout(() => {
            setErrorMessage('');
        }, 200);
        errorRef.current?.style.removeProperty('--pagination-error-max-width');
    };

    const removeRestoreHandler = () => {
        if (restoreHandlerRef.current) {
            document.removeEventListener('pointerdown', restoreHandlerRef.current);
            restoreHandlerRef.current = null;
        }
    };

    useEffect(() => {
        const input = inputRef.current;
        if (!input) {
            return;
        }
        input.value = String(pageNumber);
        input.defaultValue = String(pageNumber);
        setErrorVisible(false);
        setTimeout(() => {
            setErrorMessage('');
        }, 200);
        errorRef.current?.style.removeProperty('--pagination-error-max-width');
        removeRestoreHandler();
    }, [pageNumber]);

    useEffect(() => () => {
        removeRestoreHandler();
    }, []);

    const commitPageNumber = (input: HTMLInputElement) => {
        if (!input.validity.valid) {
            return;
        }
        const value = parseInt(input.value, 10);
        if (value !== pageNumberRef.current) {
            setPageNumber(value);
        }
        input.defaultValue = String(value);
    };

    const handlePaginationInput = (event: Event) => {
        const input = event.target as HTMLInputElement;
        if (!errorVisibleRef.current) {
            return;
        }
        if (input.validity.valid) {
            hidePaginationError();
        } else {
            input.checkValidity();
        }
    };

    const handlePaginationInvalid = (event: Event) => {
        event.preventDefault();
        const input = event.target as HTMLInputElement;
        const inputError = errorRef.current;
        if (!inputError) {
            return;
        }

        setErrorMessage(getPaginationErrorMessage(input));
        setErrorVisible(true);
        setCenteredMaxWidth(input, inputError, '--pagination-error-max-width');

        if (!restoreHandlerRef.current) {
            restoreHandlerRef.current = (pointerEvent: Event) => {
                if (!input.contains(pointerEvent.target as Node)) {
                    if (errorVisibleRef.current) {
                        input.value = String(pageNumberRef.current);
                        hidePaginationError();
                    }
                    removeRestoreHandler();
                }
            };
            setTimeout(() => {
                if (restoreHandlerRef.current) {
                    document.addEventListener('pointerdown', restoreHandlerRef.current);
                }
            }, 0);
        }
    };

    const handlePaginationBlur = (event: Event) => {
        const input = event.target as HTMLInputElement;
        if (!input.validity.valid) {
            input.checkValidity();
        } else if (input.value !== input.defaultValue) {
            commitPageNumber(input);
        }
    };

    const handlePaginationSubmit = (event: Event) => {
        event.preventDefault();
        const input = inputRef.current;
        if (!input || !input.validity.valid) {
            return;
        }
        commitPageNumber(input);
    };

    return (
        <>
            <div className={`${componentName}__components-header`}>
                <div className={`${componentName}__pagination-text`}>
                    {i18n('Showing')} {pageNumber * pageSize - pageSize + 1} - {Math.min(pageNumber * pageSize, (pageNumber * pageSize - pageSize) + components.length)} {i18n('of')} {components.length} {i18n('Results')}
                </div>
            </div>
            <div className={`${componentName}__components`}>
                {componentsLoading && !error && new Array(pageSize).fill(0).map((_, index) => (
                    <div key={index} className={`${componentName}__component`}>
                        <div className={`${componentName}__component-image ${componentName}__component-loading-skeleton ${componentName}__loading-animation`}></div>
                        <div className={`${componentName}__component-loading-skeleton ${componentName}__component-name ${componentName}__loading-animation`}></div>
                        <div className={`${componentName}__component-loading-skeleton ${componentName}__component-description ${componentName}__loading-animation`}></div>
                    </div>
                ))}
                {!componentsLoading && !error && components.filter((p, index) => index >= (pageNumber - 1) * pageSize && index < pageNumber * pageSize).map((product) => (
                    <div key={product.id} data-id={product.id} className={`${componentName}__component`}>
                        {product.preferred && <div className={`${componentName}__component-preferred-availability-tag`}>{i18n('Preferred')}</div>}
                        <div className={`${componentName}__component-image`}>
                            <img src={product.images[0].url} alt={product.images[0].alt} />
                        </div>
                        <a className={`${componentName}__component-link`} href={product.link} target="_blank">
                            <div className={`${componentName}__component-catalog-number`}>{product.id}</div>
                            <div className={`${componentName}__component-name`}>{product.name}</div>
                        </a>
                        <div className={`${componentName}__component-lifecycle-status`}>
                            <span className={`${componentName}__component-lifecycle-status-dot ${componentName}__component-lifecycle-status-dot--${product.lifeCycleStatus.toLowerCase()}`}></span>
                            <span className={`${componentName}__component-lifecycle-status-text`}>{i18n(toTitleCase(product.lifeCycleStatus))}</span>
                        </div>
                        <div className={`${componentName}__component-serial-number`}>{i18n('Serial #')}: {serialNumber}</div>
                        <div className={`${componentName}__component-description`}>{product.description}</div>
                    </div>
                ))}
                {!componentsLoading && !error && components.length === 0 && <div className={`${componentName}__no-components`}>{i18n('No components found')}</div>}
            </div>
            <div className={`${componentName}__components-footer`}>
                <div className={`${componentName}__pagination-select-container`}>
                    <label className={`${componentName}__pagination-label`}>{i18n('Items Per Page')}:</label>
                    <CustomSelect
                        componentName={componentName}
                        className={`${componentName}__pagination-select`}
                        ariaLabel={i18n('Items Per Page')}
                        value={pageSize.toString()}
                        options={[
                            { value: '24', label: i18n('24') },
                            { value: '48', label: i18n('48') },
                            { value: '72', label: i18n('72') },
                        ]}
                        onChange={(value) => setPageSize(parseInt(value || '24'))}
                        i18n={i18n}
                    />
                </div>
                <div className={`${componentName}__pagination`}>
                    <div className={`${componentName}__pagination-text`}>
                        {pageNumber * pageSize - pageSize + 1} - {Math.min(pageNumber * pageSize, (pageNumber * pageSize - pageSize) + components.length)} {i18n('of')} {components.length}
                    </div>
                    <div className={`${componentName}__pagination-input-container`}>
                        <button type="button" className={`${componentName}__pagination-button`} onClick={() => setPageNumber(pageNumber - 1)} disabled={pageNumber === 1} aria-label={i18n('Previous')}>
                            <svg className={`${componentName}__pagination-button-icon`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" aria-hidden="true" style={{ visibility: pageNumber === 1 ? 'hidden' : 'visible' }}>
                                <path d="M14.08 15.606l5.455-8.063a.8.8 0 1 0-1.325-.897l-5.745 8.491a.799.799 0 0 0-.018.868l5.745 9.32a.8.8 0 1 0 1.362-.84l-5.474-8.879z" />
                            </svg>
                        </button>
                        <form className={`${componentName}__pagination-input-wrapper`} onSubmit={handlePaginationSubmit}>
                            <input
                                ref={inputRef}
                                className={`${componentName}__pagination-input`}
                                type="number"
                                name="input"
                                min="1"
                                max={totalPages}
                                step="1"
                                defaultValue={pageNumber}
                                required
                                aria-describedby={paginationErrorId}
                                data-error-min={i18n('Value must be greater than or equal to')}
                                data-error-max={i18n('Value must be less than or equal to')}
                                data-error-number={i18n('Please enter a number.')}
                                onInput={handlePaginationInput}
                                onInvalid={handlePaginationInvalid}
                                onBlur={handlePaginationBlur}
                            />
                            <div
                                ref={errorRef}
                                id={paginationErrorId}
                                className={`${componentName}__pagination-error${errorVisible ? ' is-visible' : ''}`}
                                role="alert"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" aria-hidden="true" focusable="false">
                                    <path d="M30.684 29.19H1.316a.816.816 0 0 1-.702-1.231L15.29 3.225a.815.815 0 0 1 1.43.012l14.667 24.722a.816.816 0 0 1-.702 1.232zM2.748 27.56h26.503L16 5.223 2.748 27.56z" />
                                    <path d="M16.044 21.167a.816.816 0 0 1-.816-.816v-8.614a.816.816 0 1 1 1.632 0v8.614c0 .45-.365.816-.816.816zM14.964 24.684a1.142 1.142 0 1 0 2.07-.961 1.142 1.142 0 0 0-2.07.96z" />
                                </svg>
                                <span className={`${componentName}__pagination-error-text`}>{errorMessage}</span>
                            </div>
                        </form>
                        {i18n('of')} {totalPages}
                        <button type="button" className={`${componentName}__pagination-button`} onClick={() => setPageNumber(pageNumber + 1)} disabled={components.length < pageSize} aria-label={i18n('Next')}>
                            <svg className={`${componentName}__pagination-button-icon`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" aria-hidden="true" style={{ visibility: components.length < pageSize ? 'hidden' : 'visible' }}>
                                <path d="M12.446 7.514l5.474 8.88-5.455 8.063a.8.8 0 1 0 1.325.897l5.745-8.491a.799.799 0 0 0 .018-.868l-5.745-9.32a.8.8 0 0 0-1.362.839z" />
                            </svg>
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
}

export default ComponentsSection;
